/**
 * Defensive cleanup against the "underhive" visual/web editor.
 *
 * This app used to import `underhive` and call `attachEditor()` directly
 * (see git history of src/main.tsx). That integration has been removed, and
 * the `underhive` dependency has been dropped from package.json.
 *
 * This module is NOT a re-implementation of that integration and it must
 * never call the editor's companion dev-server endpoint or any other
 * network endpoint. It exists purely as a safety net in case some external
 * dev tooling outside this repo (e.g. a browser extension or an injected
 * script from the hosting/dev environment) reintroduces the editor's
 * `<uh-web-editor>` custom element at runtime. If nothing ever injects it,
 * this module has no visible or behavioral effect on the app.
 */

const EDITOR_TAG = 'uh-web-editor'
const WATCH_DURATION_MS = 5000

function removeEditorElements(): void {
  document.querySelectorAll(EDITOR_TAG).forEach((el) => el.remove())
}

export function removeInjectedEditor(): void {
  if (typeof document === 'undefined') return

  removeEditorElements()

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return
        const el = node as Element
        if (el.tagName.toLowerCase() === EDITOR_TAG) {
          el.remove()
        }
      })
    }
  })

  observer.observe(document.documentElement, { childList: true, subtree: true })

  window.setTimeout(() => observer.disconnect(), WATCH_DURATION_MS)
}

removeInjectedEditor()
